"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { cn } from "@/src/lib/utils";

gsap.registerPlugin(ScrollTrigger);

interface StickyColumnProps {
  children: React.ReactNode;
  className?: string;
  boundsRef: React.RefObject<HTMLElement | null>;
  /** Distance from viewport top to pin at, in px. Defaults to the --header-height theme token. */
  offset?: number;
  /** Minimum viewport width, in px, at which pinning activates. Below this it flows normally. */
  minWidth?: number;
}

/**
 * Only pins when the bounds column is taller than one viewport — a short
 * right column doesn't leave enough scroll distance for pinning to read as
 * anything but a jittery stick-then-instantly-unstick, so it's left as
 * normal flow instead.
 */
export function StickyColumn({
  children,
  className,
  boundsRef,
  offset,
  minWidth = 768,
}: StickyColumnProps) {
  const pinRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const pinTarget = pinRef.current;
    const bounds = boundsRef.current;
    if (!pinTarget || !bounds) return;

    const mediaQuery = window.matchMedia(`(min-width: ${minWidth}px)`);
    let trigger: ScrollTrigger | null = null;

    const shouldPin = () => mediaQuery.matches && bounds.offsetHeight > window.innerHeight;

    const create = () => {
      const resolvedOffset =
        offset ??
        (parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue("--header-height"),
        ) || 0);

      trigger = ScrollTrigger.create({
        trigger: pinTarget,
        start: `top ${resolvedOffset}px`,
        endTrigger: bounds,
        end: `bottom bottom-=${resolvedOffset}px`,
        pin: true,
        pinSpacing: false,
      });
    };

    const destroy = () => {
      trigger?.kill();
      trigger = null;
    };

    const evaluate = () => {
      const wants = shouldPin();
      if (wants && !trigger) create();
      else if (!wants && trigger) destroy();
      else if (wants && trigger) ScrollTrigger.refresh();
    };

    evaluate();

    const resizeObserver = new ResizeObserver(evaluate);
    resizeObserver.observe(bounds);
    mediaQuery.addEventListener("change", evaluate);
    window.addEventListener("resize", evaluate);

    // Sibling sections (e.g. a data-driven one rendering `null`) can leave
    // this trigger's start/end computed against a page layout that hasn't
    // fully settled yet — every instance on the page schedules one global
    // recompute for after all of them have mounted, so positions match the
    // final DOM regardless of which sections ended up present.
    const refreshFrame = requestAnimationFrame(() => ScrollTrigger.refresh());

    return () => {
      destroy();
      resizeObserver.disconnect();
      mediaQuery.removeEventListener("change", evaluate);
      window.removeEventListener("resize", evaluate);
      cancelAnimationFrame(refreshFrame);
    };
  }, [boundsRef, offset, minWidth]);

  return (
    <div ref={pinRef} className={cn(className)}>
      {children}
    </div>
  );
}
